import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/hooks/useAuth.ts';
import { useWebSocketSubscription } from '@/hooks/useWebSocketSubscription.ts';
import { useWebSocketEvent } from '@/hooks/useWebSocketEvent.ts';
import { WebSocketEventType } from '@/types/websocket.ts';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import { SeverityBadge } from '@/components/SeverityBadge.tsx';
import {
  useNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  type AppNotification,
} from '@/hooks/useNotifications.ts';

/**
 * Inbox bell in the TopBar. Shows an unread badge, a dropdown of recent
 * notifications, mark-read on click-through, and mark-all-read. The inbox is the
 * firehose (every relevant CM event lands here); this is the read/ack surface.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Unread only. The bell is the "what needs me now" surface; the complete record,
  // read rows included, is the inbox behind "See all notifications". Reading a row
  // therefore removes it from this list — that is the point of it.
  const { data, isLoading } = useNotifications(true, 30);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  // Realtime: subscribe to this user's own channel and refresh the inbox the
  // instant a notification lands (the 60s poll is the fallback).
  useWebSocketSubscription(user ? { type: 'user', userId: user.id } : null, !!user);
  useWebSocketEvent(
    WebSocketEventType.NOTIFICATION_CREATED,
    () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  );

  const notifications = data?.notifications ?? [];
  const unread = data?.unreadCount ?? 0;

  const onItemClick = (n: AppNotification) => {
    if (!n.read) markRead.mutate(n.id);
    if (n.link) {
      setOpen(false);
      void navigate(n.link);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative inline-flex h-11 w-11 md:h-10 md:w-10 items-center justify-center rounded-md text-foreground hover:bg-accent"
          aria-label={unread > 0 ? `Notifications (${String(unread)} unread)` : 'Notifications'}
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute top-1.5 right-1.5 inline-flex min-w-4 h-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[28rem] p-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border-subtle">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => { markAllRead.mutate(); }}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </button>
          )}
        </div>
        {/* Only this list scrolls — the header above and the "See all notifications"
            footer below are siblings, so they stay put. `auto` keeps the bar painted
            whenever there is more below, without a dead track on a two-row list. */}
        <ScrollArea type="auto" className="max-h-96">
          {isLoading ? (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">Loading…</div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-8 text-sm text-muted-foreground text-center">You're all caught up.</div>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => { onItemClick(n); }}
                    className={`w-full text-left px-4 py-3 hover:bg-accent transition-colors ${
                      n.read ? '' : 'bg-accent/40'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />}
                      <div className={`flex-1 min-w-0 ${n.read ? 'pl-4' : ''}`}>
                        <div className="flex items-center gap-1.5">
                          {/* A resolution's `severity` is the peak of the incident that
                              CLOSED, carried so a critical-only rule still hears the
                              ending. Rendering it as a severity chip made "the device
                              came back" wear the same critical badge as the outage, so
                              the resolved chip replaces it rather than sitting beside
                              it. Absent on rows written before the inbox carried
                              severity, rather than defaulted to a classification nobody
                              made. */}
                          {n.isResolution ? (
                            <span className="shrink-0 rounded border border-success/40 bg-success/10 px-1.5 py-0.5 text-[10px] font-medium text-success">
                              Resolved
                            </span>
                          ) : n.severity ? (
                            <SeverityBadge severity={n.severity} className="text-[10px] shrink-0" />
                          ) : null}
                          {/* Wraps rather than truncates: these titles carry the gateway or
                              device identity at the end ("… 'GarageAP' offline"), which is the
                              part an ellipsis eats. min-w-0 so the title can shrink below its
                              content width inside the flex row. */}
                          <div className="min-w-0 flex-1 text-sm font-medium break-words">{n.title}</div>
                        </div>
                        {n.body && <div className="text-xs text-muted-foreground line-clamp-2 break-words">{n.body}</div>}
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                        </div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
        {/* The popover shows the newest thirty and always has. Until the inbox page
            existed there was nowhere to send someone who wanted the rest. */}
        <div className="border-t border-border-subtle px-4 py-2">
          <Link
            to="/notifications"
            onClick={() => { setOpen(false); }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            See all notifications
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
